import { useRef, useState } from "react";

const API_URL = process.env.REACT_APP_SERVER_URL;
const DEFAULT_FOLDER = "onceUpOnAMat/coverVideoPhotos";

// onUploaded({ newUrl, replacedUrl }) — replacedUrl is set when this component
// uploaded something previously and the user is now replacing it
const CloudinaryImageUpload = ({ label, value, onChange, onUploaded, token, placeholder = "https://...", folder = DEFAULT_FOLDER, center = false }) => {
  const [uploading, setUploading] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef(null);
  // tracks the URL most recently uploaded by this component instance
  const lastUploadedRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) uploadFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!uploading) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (uploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file?.type.startsWith("image/")) uploadFile(file);
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const sigRes = await fetch(`${API_URL}/admin/cloudinary/signature`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ folder }),
      });
      if (!sigRes.ok) throw new Error("Failed to get upload signature");
      const { signature, timestamp, apiKey, cloudName } = await sigRes.json();

      const formData = new FormData();
      formData.append("file", file);
      formData.append("api_key", apiKey);
      formData.append("timestamp", timestamp);
      formData.append("signature", signature);
      formData.append("folder", folder);

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        { method: "POST", body: formData }
      );
      if (!uploadRes.ok) throw new Error("Upload to Cloudinary failed");
      const data = await uploadRes.json();
      const newUrl = data.secure_url;

      const replacedUrl = lastUploadedRef.current || null;
      lastUploadedRef.current = newUrl;

      onChange(newUrl);
      onUploaded?.({ newUrl, replacedUrl });
    } catch (err) {
      alert(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    const clearedUrl = value;
    const wasUploadedByUs = lastUploadedRef.current === clearedUrl;
    lastUploadedRef.current = null;
    onChange("");
    if (wasUploadedByUs) {
      onUploaded?.({ newUrl: null, replacedUrl: clearedUrl });
    }
  };

  return (
    <div>
      {label && (
        <label className={`block text-sm font-medium text-gray-700 mb-1.5 ${center ? "text-center" : ""}`}>{label}</label>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      {value ? (
        <div className={`space-y-2 ${center ? "flex flex-col items-center" : ""}`}>
          <div className="relative inline-block">
            <img
              src={value}
              alt="Cover"
              className="h-24 w-auto max-w-full rounded-md border border-gray-200 object-cover bg-gray-50"
            />
            <button
              type="button"
              onClick={handleClear}
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 transition"
              title="Remove"
            >
              ×
            </button>
          </div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={uploading}
            className={`block text-xs text-brown hover:underline disabled:opacity-50 ${isDragging ? "underline" : ""}`}
          >
            {uploading ? "Uploading..." : isDragging ? "Drop to replace" : "Replace image"}
          </button>
        </div>
      ) : (
        <div className={`space-y-2 ${center ? "flex flex-col items-center" : ""}`}>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            disabled={uploading}
            className={`flex items-center gap-2 px-3 py-2 border border-dashed rounded-md text-sm transition disabled:opacity-50 disabled:cursor-not-allowed w-full justify-center ${
              isDragging
                ? "border-brown text-brown bg-gray-50"
                : "border-gray-300 text-gray-600 hover:border-brown hover:text-brown hover:bg-gray-50"
            }`}
          >
            {uploading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Uploading...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {isDragging ? "Drop to upload" : "Upload image"}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowUrlInput((v) => !v)}
            className="block text-xs text-gray-400 hover:text-gray-600 mx-auto"
          >
            {showUrlInput ? "Hide URL input" : "or paste URL instead"}
          </button>
          {showUrlInput && (
            <input
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-brown focus:border-brown"
              placeholder={placeholder}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CloudinaryImageUpload;
